/**
 * FileTransferService - P2P file sharing with progress tracking
 * Extracted and enhanced from realP2PDemo.html file sharing functionality
 * Handles file uploads, downloads, progress tracking, and chunked transfers
 */

import { DataConnection } from 'peerjs'
import { toastManager, TOAST_GROUPS } from '../utils/toastManager'
import { EventEmitter } from './EventEmitter'

export interface FileInfo {
  id: string
  name: string
  size: number
  type: string
}

export interface FileTransferRequest {
  type: 'file-transfer-request'
  from: string
  fromAlias: string
  fileInfo: FileInfo
  timestamp: number
}

export interface FileTransferResponse {
  type: 'file-transfer-response'
  from: string
  fromAlias: string
  transferId: string
  accepted: boolean
  timestamp: number
}

export interface FileChunk {
  type: 'file-chunk'
  transferId: string
  chunkIndex: number
  totalChunks: number
  data: ArrayBuffer
  timestamp: number
}

export interface FileTransferComplete {
  type: 'file-transfer-complete'
  transferId: string
  timestamp: number
}

export interface FileTransfer {
  id: string
  fileInfo: FileInfo
  peerId: string
  peerAlias: string
  direction: 'sending' | 'receiving'
  status: 'pending' | 'accepted' | 'rejected' | 'transferring' | 'completed' | 'failed' | 'cancelled'
  progress: number
  startTime: Date | null
  completedTime: Date | null
  file?: File // For sending
  chunks?: ArrayBuffer[] // For receiving
  totalChunks?: number
  receivedChunks?: number
}

export class FileTransferService extends EventEmitter {
  // Removed unused fields: _localPeerId and _localAlias
  private autoAcceptFiles = false // Option for auto-accepting file transfers
  private connectionFinder?: (peerId: string) => DataConnection | null
  
  constructor(_localPeerId: string, _localAlias: string) {
    super()
    // Parameters accepted for interface compatibility but not stored
  }
  private transfers = new Map<string, FileTransfer>()
  private transferListeners: ((transfers: FileTransfer[]) => void)[] = []
  // MOBILE FIX: Adaptive chunk sizes based on device type
  private readonly CHUNK_SIZE = this.getOptimalChunkSize()
  private readonly MAX_CONCURRENT_TRANSFERS = this.getMaxConcurrentTransfers()
  private readonly MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB limit
  private readonly TIMEOUT_DURATION = 60000 // 60 seconds timeout
  // MOBILE FIX: Message size limits for different connection types
  private readonly MAX_MESSAGE_SIZE = 15000 // 15KB safe limit for PeerJS
  // private readonly MOBILE_CHUNK_SIZE = 32 * 1024 // 32KB for mobile - Unused

  /**
   * Initiate file transfer to a peer with improved validation
   * Extracted from startFileTransfer() function in realP2PDemo.html line 4915
   */
  async initiateFileTransfer(
    file: File,
    targetPeerId: string,
    targetAlias: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection
  ): Promise<string> {
    if (!connection.open) {
      throw new Error('Connection not open')
    }

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      const errorMsg = `File too large: ${this.formatFileSize(file.size)}. Maximum allowed: ${this.formatFileSize(this.MAX_FILE_SIZE)}`
      console.error(errorMsg)
      toastManager.error('File too large', TOAST_GROUPS.FILE_TRANSFER)
      throw new Error(errorMsg)
    }

    if (file.size === 0) {
      const errorMsg = 'Cannot transfer empty file'
      console.error(errorMsg)
      toastManager.error('Cannot send empty file', TOAST_GROUPS.FILE_TRANSFER)
      throw new Error(errorMsg)
    }

    // Check concurrent transfer limit
    const activeTransfers = this.getActiveTransfers()
    if (activeTransfers.length >= this.MAX_CONCURRENT_TRANSFERS) {
      const errorMsg = `Maximum concurrent transfers (${this.MAX_CONCURRENT_TRANSFERS}) reached`
      console.error(errorMsg)
      toastManager.error('Too many active transfers', TOAST_GROUPS.FILE_TRANSFER)
      throw new Error(errorMsg)
    }

    const transferId = `transfer-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`
    const fileInfo: FileInfo = {
      id: transferId,
      name: file.name,
      size: file.size,
      type: file.type
    }

    const transfer: FileTransfer = {
      id: transferId,
      fileInfo,
      peerId: targetPeerId,
      peerAlias: targetAlias,
      direction: 'sending',
      status: 'pending',
      progress: 0,
      startTime: null,
      completedTime: null,
      file
    }

    this.transfers.set(transferId, transfer)

    // MOBILE FIX: Validate and optimize file transfer request
    const requestData: FileTransferRequest = {
      type: 'file-transfer-request',
      from: fromPeerId,
      fromAlias,
      fileInfo: this.optimizeFileInfo(fileInfo),
      timestamp: Date.now()
    }

    // MOBILE FIX: Validate message size before sending
    if (!this.validateMessageSize(requestData)) {
      throw new Error('File transfer request too large for connection')
    }

    try {
      connection.send(requestData)
      console.log(`📁 File transfer requested: ${file.name} (${this.formatFileSize(file.size)}) to ${targetAlias}`)
      toastManager.info(`Requesting to send ${file.name} (${this.formatFileSize(file.size)}) to ${targetAlias}`, TOAST_GROUPS.FILE_TRANSFER)
      
      // Set transfer timeout
      setTimeout(() => {
        const transfer = this.transfers.get(transferId)
        if (transfer && transfer.status === 'pending') {
          transfer.status = 'failed'
          console.log(`📁 File transfer request timeout: ${file.name}`)
          console.error(`File transfer request timeout for ${file.name}`)
          toastManager.error('File transfer timeout', TOAST_GROUPS.FILE_TRANSFER)
          this.notifyTransferListeners()
        }
      }, this.TIMEOUT_DURATION)
      
      this.notifyTransferListeners()
      return transferId
    } catch (error) {
      this.transfers.delete(transferId)
      console.error('Failed to send file transfer request:', error)
      throw error
    }
  }

  /**
   * Accept incoming file transfer
   */
  acceptFileTransfer(
    transferId: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection
  ): void {
    const responseData: FileTransferResponse = {
      type: 'file-transfer-response',
      from: fromPeerId,
      fromAlias,
      transferId,
      accepted: true,
      timestamp: Date.now()
    }

    connection.send(responseData)
    console.log(`✅ File transfer accepted: ${transferId}`)
    console.log(`File transfer accepted`)
    toastManager.success('Transfer accepted', TOAST_GROUPS.FILE_TRANSFER)
  }

  /**
   * Reject incoming file transfer
   */
  rejectFileTransfer(
    transferId: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection
  ): void {
    const responseData: FileTransferResponse = {
      type: 'file-transfer-response',
      from: fromPeerId,
      fromAlias,
      transferId,
      accepted: false,
      timestamp: Date.now()
    }

    connection.send(responseData)
    console.log(`❌ File transfer rejected: ${transferId}`)
    console.log(`File transfer rejected`)
    toastManager.error('Transfer rejected', TOAST_GROUPS.FILE_TRANSFER)
  }

  /**
   * Start actual file transfer with improved error handling and progress tracking
   */
  private async startFileTransfer(transferId: string, connection: DataConnection): Promise<void> {
    const transfer = this.transfers.get(transferId)
    if (!transfer || !transfer.file) {
      throw new Error('Transfer not found or no file')
    }

    if (!connection || !connection.open) {
      transfer.status = 'failed'
      console.error('Connection not available for file transfer')
      console.error('Connection lost - file transfer failed')
      toastManager.error('Transfer failed - connection lost', TOAST_GROUPS.FILE_TRANSFER)
      this.notifyTransferListeners()
      return
    }

    transfer.status = 'transferring'
    transfer.startTime = new Date()
    
    const file = transfer.file
    const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE)
    transfer.totalChunks = totalChunks

    console.log(`📤 Starting file transfer: ${file.name} (${totalChunks} chunks, ${this.formatFileSize(file.size)})`)
    toastManager.info(`Starting file transfer: ${file.name}`, TOAST_GROUPS.FILE_TRANSFER, { icon: '📤' })

    let lastProgressUpdate = 0

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        // Check if transfer was cancelled
        const currentTransfer = this.transfers.get(transferId)
        if (!currentTransfer || currentTransfer.status === 'cancelled') {
          console.log(`📁 File transfer cancelled: ${file.name}`)
          return
        }

        // Check connection is still open
        if (!connection.open) {
          throw new Error('Connection lost during file transfer')
        }

        const start = chunkIndex * this.CHUNK_SIZE
        const end = Math.min(start + this.CHUNK_SIZE, file.size)
        const chunk = file.slice(start, end)
        
        let arrayBuffer: ArrayBuffer
        try {
          arrayBuffer = await chunk.arrayBuffer()
        } catch (error) {
          throw new Error(`Failed to read file chunk ${chunkIndex}: ${error}`)
        }

        const chunkData: FileChunk = {
          type: 'file-chunk',
          transferId,
          chunkIndex,
          totalChunks,
          data: arrayBuffer,
          timestamp: Date.now()
        }

        // MOBILE FIX: Validate chunk size before sending
        if (!this.validateChunkSize(chunkData)) {
          throw new Error(`Chunk ${chunkIndex} too large for connection`)
        }

        try {
          connection.send(chunkData)
        } catch (error) {
          // MOBILE FIX: Enhanced error handling for mobile connections
          if (error instanceof Error && error.toString().includes('Message too big')) {
            throw new Error(`Chunk ${chunkIndex} exceeds connection limits - try smaller files`)
          }
          throw new Error(`Failed to send chunk ${chunkIndex}: ${error}`)
        }

        // Update progress
        transfer.progress = Math.round(((chunkIndex + 1) / totalChunks) * 100)
        
        // Only notify listeners every 5% progress or every 10 chunks to avoid too frequent updates
        if (transfer.progress - lastProgressUpdate >= 5 || chunkIndex % 10 === 0 || chunkIndex === totalChunks - 1) {
          this.notifyTransferListeners()
          lastProgressUpdate = transfer.progress
        }

        // Adaptive delay based on connection performance
        const delay = file.size > 10 * 1024 * 1024 ? 20 : 10 // Longer delay for large files
        await new Promise(resolve => setTimeout(resolve, delay))
      }

      // Send completion signal
      const completeData: FileTransferComplete = {
        type: 'file-transfer-complete',
        transferId,
        timestamp: Date.now()
      }
      
      try {
        connection.send(completeData)
      } catch (error) {
        throw new Error(`Failed to send completion signal: ${error}`)
      }

      transfer.status = 'completed'
      transfer.completedTime = new Date()
      transfer.progress = 100

      const duration = transfer.completedTime.getTime() - (transfer.startTime?.getTime() || 0)
      const speed = file.size / (duration / 1000) // bytes per second
      
      console.log(`✅ File transfer completed: ${file.name} in ${(duration / 1000).toFixed(1)}s (${this.formatFileSize(speed)}/s)`)
      console.log(`File sent: ${file.name} (${this.formatFileSize(speed)}/s)`)
      toastManager.success('File sent successfully', TOAST_GROUPS.FILE_TRANSFER)

    } catch (error) {
      transfer.status = 'failed'
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error(`File transfer failed for ${file.name}:`, errorMsg)
      console.error(`File transfer failed: ${errorMsg}`)
      toastManager.error('File transfer failed', TOAST_GROUPS.FILE_TRANSFER)
    }

    this.notifyTransferListeners()
  }

  /**
   * Handle incoming file transfer request with improved validation
   */
  handleFileTransferRequest(data: FileTransferRequest, fromPeerId: string): void {
    // Validate file size
    if (data.fileInfo.size > this.MAX_FILE_SIZE) {
      console.error(`Incoming file too large: ${this.formatFileSize(data.fileInfo.size)}`)
      console.error(`File from ${data.fromAlias} is too large (${this.formatFileSize(data.fileInfo.size)}). Maximum: ${this.formatFileSize(this.MAX_FILE_SIZE)}`)
      toastManager.error('File too large', TOAST_GROUPS.FILE_TRANSFER)
      return
    }

    if (data.fileInfo.size === 0) {
      console.error('Incoming file is empty')
      console.error(`Cannot receive empty file from ${data.fromAlias}`)
      toastManager.error('Cannot receive empty file', TOAST_GROUPS.FILE_TRANSFER)
      return
    }

    // Check if we already have this transfer
    if (this.transfers.has(data.fileInfo.id)) {
      console.warn(`Transfer ${data.fileInfo.id} already exists`)
      return
    }

    // Check concurrent transfer limit
    const activeTransfers = this.getActiveTransfers()
    if (activeTransfers.length >= this.MAX_CONCURRENT_TRANSFERS) {
      console.warn(`Maximum concurrent transfers reached, rejecting ${data.fileInfo.name}`)
      console.warn(`Cannot receive ${data.fileInfo.name} - too many active transfers`)
      toastManager.error('Too many active transfers', TOAST_GROUPS.FILE_TRANSFER)
      return
    }

    const transfer: FileTransfer = {
      id: data.fileInfo.id,
      fileInfo: data.fileInfo,
      peerId: fromPeerId,
      peerAlias: data.fromAlias,
      direction: 'receiving',
      status: 'pending',
      progress: 0,
      startTime: null,
      completedTime: null,
      chunks: [],
      totalChunks: 0,
      receivedChunks: 0
    }

    this.transfers.set(data.fileInfo.id, transfer)

    console.log(`📁 Incoming file transfer: ${data.fileInfo.name} (${this.formatFileSize(data.fileInfo.size)}) from ${data.fromAlias}`)
    
    // Auto-accept small files if enabled
    if (this.autoAcceptFiles && data.fileInfo.size < 1024 * 1024) { // Auto-accept files under 1MB
      setTimeout(() => {
        const connection = this.findConnectionForPeer(fromPeerId)
        if (connection) {
          this.acceptFileTransfer(data.fileInfo.id, fromPeerId, data.fromAlias, connection)
        }
      }, 100) // Small delay to ensure UI updates
    }
    
    // Show file transfer request notification
    const message = `File transfer from ${data.fromAlias}: ${data.fileInfo.name} (${this.formatFileSize(data.fileInfo.size)})`
    toastManager.success(message, TOAST_GROUPS.FILE_TRANSFER, { duration: 30000 }) // 30 second timeout
    
    // Emit event for UI components
    this.emit('fileTransferRequest', {
      transfer,
      fromPeerId,
      fromAlias: data.fromAlias
    })

    this.notifyTransferListeners()
  }

  /**
   * Handle file transfer response
   */
  handleFileTransferResponse(data: FileTransferResponse, connection: DataConnection): void {
    const transfer = this.transfers.get(data.transferId)
    if (!transfer) return

    if (data.accepted) {
      console.log(`✅ File transfer accepted by ${data.fromAlias}`)
      transfer.status = 'accepted'
      this.startFileTransfer(data.transferId, connection)
    } else {
      console.log(`❌ File transfer rejected by ${data.fromAlias}`)
      transfer.status = 'rejected'
      console.log(`File transfer rejected by ${data.fromAlias}`)
      toastManager.error('File transfer rejected', TOAST_GROUPS.FILE_TRANSFER)
    }

    this.notifyTransferListeners()
  }

  /**
   * Handle incoming file chunk with memory management for large files
   */
  handleFileChunk(data: FileChunk): void {
    const transfer = this.transfers.get(data.transferId)
    if (!transfer || transfer.direction !== 'receiving') return

    if (!transfer.chunks) {
      transfer.chunks = new Array(data.totalChunks)
      transfer.totalChunks = data.totalChunks
      transfer.receivedChunks = 0
      transfer.status = 'transferring'
      transfer.startTime = new Date()
    }

    // Store chunk
    transfer.chunks[data.chunkIndex] = data.data
    transfer.receivedChunks = (transfer.receivedChunks || 0) + 1

    // MEMORY MANAGEMENT: Implement progressive cleanup for large files
    if (transfer.fileInfo.size > 50 * 1024 * 1024) { // 50MB threshold
      const batchSize = 100 // Process chunks in batches of 100
      const currentBatch = Math.floor(data.chunkIndex / batchSize)
      const isLastChunkInBatch = (data.chunkIndex + 1) % batchSize === 0 ||
                                 data.chunkIndex === data.totalChunks - 1

      // Progressive memory cleanup: remove old chunks to prevent memory buildup
      if (isLastChunkInBatch && currentBatch > 1) {
        const startIdx = Math.max(0, (currentBatch - 2) * batchSize)
        const endIdx = (currentBatch - 1) * batchSize

        // Properly dispose of processed chunks
        for (let i = startIdx; i < endIdx; i++) {
          if (transfer.chunks[i]) {
            // Clear chunk data and remove reference
            delete transfer.chunks[i]
          }
        }

        // Suggest garbage collection if available
        if (typeof globalThis.gc === 'function') {
          globalThis.gc()
        }
      }
    }

    // Update progress
    transfer.progress = Math.round((transfer.receivedChunks / data.totalChunks) * 100)

    console.log(`📦 Received chunk ${data.chunkIndex + 1}/${data.totalChunks} for ${transfer.fileInfo.name}`)

    this.notifyTransferListeners()
  }

  /**
   * Handle file transfer completion
   */
  handleFileTransferComplete(data: FileTransferComplete): void {
    const transfer = this.transfers.get(data.transferId)
    if (!transfer || transfer.direction !== 'receiving') return

    if (transfer.chunks && transfer.receivedChunks === transfer.totalChunks) {
      // Reconstruct file from all chunks (filtering out disposed chunks)
      const chunks = transfer.chunks.filter(chunk => chunk !== undefined && chunk !== null)

      try {
        const blob = new Blob(chunks, { type: transfer.fileInfo.type })

        // Trigger download with proper cleanup
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = transfer.fileInfo.name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        // Clean up blob URL immediately after use
        URL.revokeObjectURL(url)

        // Clear blob reference to help with garbage collection
        // The blob variable will go out of scope naturally
      } catch (error) {
        console.error('Failed to create download blob:', error)
        toastManager.error('Download failed', TOAST_GROUPS.FILE_TRANSFER)
        transfer.status = 'failed'
        this.disposeTransferChunks(transfer)
        transfer.chunks = []
        return
      }

      transfer.status = 'completed'
      transfer.completedTime = new Date()
      transfer.progress = 100

      // MEMORY MANAGEMENT: Comprehensive cleanup after successful transfer
      this.disposeTransferChunks(transfer)
      transfer.chunks = [] // Clear chunk data to free memory

      console.log(`✅ File received and downloaded: ${transfer.fileInfo.name}`)
      console.log(`File received: ${transfer.fileInfo.name}`)
      toastManager.success('File received', TOAST_GROUPS.FILE_TRANSFER)
    } else {
      transfer.status = 'failed'
      // MEMORY MANAGEMENT: Clean up chunk memory on failure
      if (transfer.chunks) {
        this.disposeTransferChunks(transfer)
        transfer.chunks = []
      }
      console.error('File transfer incomplete - missing chunks')
      console.error('File transfer failed - missing data')
      toastManager.error('File transfer failed', TOAST_GROUPS.FILE_TRANSFER)
    }

    this.notifyTransferListeners()
  }

  /**
   * Cancel file transfer with memory cleanup
   */
  cancelFileTransfer(transferId: string): void {
    const transfer = this.transfers.get(transferId)
    if (transfer) {
      transfer.status = 'cancelled'

      // MEMORY MANAGEMENT: Comprehensive cleanup on cancellation
      if (transfer.chunks) {
        this.disposeTransferChunks(transfer)
        transfer.chunks = []
      }
      // Clear file reference for sending transfers
      if (transfer.file) {
        this.disposeFileReference(transfer)
        delete transfer.file
      }

      console.log(`📁 File transfer cancelled: ${transfer.fileInfo.name}`)
      toastManager.info('File transfer cancelled', TOAST_GROUPS.FILE_TRANSFER, { icon: '❌' })
      this.notifyTransferListeners()
    }
  }

  /**
   * Get all file transfers
   */
  getTransfers(): FileTransfer[] {
    return Array.from(this.transfers.values())
  }

  /**
   * Get active transfers
   */
  getActiveTransfers(): FileTransfer[] {
    return this.getTransfers().filter(t => 
      ['pending', 'accepted', 'transferring'].includes(t.status)
    )
  }

  /**
   * Handle P2P data (router method)
   */
  handleP2PData(data: unknown, fromPeerId: string, connection?: DataConnection): boolean {
    if (typeof data !== 'object' || !data) {
      return false
    }

    const typedData = data as { type?: string }

    switch (typedData.type) {
      case 'file-transfer-request':
        this.handleFileTransferRequest(typedData as FileTransferRequest, fromPeerId)
        return true

      case 'file-transfer-response':
        if (connection) {
          this.handleFileTransferResponse(typedData as FileTransferResponse, connection)
        }
        return true

      case 'file-chunk':
        this.handleFileChunk(typedData as FileChunk)
        return true

      case 'file-transfer-complete':
        this.handleFileTransferComplete(typedData as FileTransferComplete)
        return true

      default:
        return false
    }
  }

  /**
   * Add transfer listener
   */
  onTransfersChange(listener: (transfers: FileTransfer[]) => void): () => void {
    this.transferListeners.push(listener)
    return () => {
      const index = this.transferListeners.indexOf(listener)
      if (index > -1) {
        this.transferListeners.splice(index, 1)
      }
    }
  }

  /**
   * Format file size
   * Extracted from formatFileSize() function in realP2PDemo.html line 4970
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Get transfer statistics
   */
  getStats(): {
    totalTransfers: number
    activeTransfers: number
    completedTransfers: number
    totalDataTransferred: number
  } {
    const transfers = this.getTransfers()
    const completed = transfers.filter(t => t.status === 'completed')
    const totalDataTransferred = completed.reduce((sum, t) => sum + t.fileInfo.size, 0)

    return {
      totalTransfers: transfers.length,
      activeTransfers: this.getActiveTransfers().length,
      completedTransfers: completed.length,
      totalDataTransferred
    }
  }

  /**
   * Notify transfer listeners
   */
  private notifyTransferListeners(): void {
    this.transferListeners.forEach(listener => {
      listener(this.getTransfers())
    })
  }

  /**
   * Set auto-accept file transfers option
   */
  setAutoAcceptFiles(autoAccept: boolean): void {
    this.autoAcceptFiles = autoAccept
    console.log(`📁 Auto-accept files: ${autoAccept ? 'enabled' : 'disabled'}`)
  }

  /**
   * Get auto-accept setting
   */
  getAutoAcceptFiles(): boolean {
    return this.autoAcceptFiles
  }

  /**
   * Set connection finder function (injected by PeerConnectionManager)
   */
  setConnectionFinder(finder: (peerId: string) => DataConnection | null): void {
    this.connectionFinder = finder
  }

  /**
   * Find connection for a peer (helper method)
   * This should be injected or managed by PeerConnectionManager
   */
  private findConnectionForPeer(peerId: string): DataConnection | null {
    return this.connectionFinder ? this.connectionFinder(peerId) : null
  }

  /**
   * Update local peer information
   */
  updateLocalPeer(_peerId: string, _alias: string): void {
    // No-op since local peer fields are no longer stored
  }

  /**
   * Properly dispose of transfer chunks to prevent memory leaks
   */
  private disposeTransferChunks(transfer: FileTransfer): void {
    if (!transfer.chunks) return

    // For ArrayBuffer chunks, use transfer() method if available for better performance
    transfer.chunks.forEach((chunk, index) => {
      if (chunk) {
        try {
          // If chunk is ArrayBuffer and transfer() is available, use it
          if (chunk instanceof ArrayBuffer && 'transfer' in chunk) {
            // Transfer ownership to a new buffer, making the original one detached
            (chunk as any).transfer(0) // Transfer ownership for GC
            // The original ArrayBuffer is now detached and can be garbage collected
          }
          // Clear the reference
          transfer.chunks![index] = undefined as any
        } catch (error) {
          // Fallback: just clear the reference
          transfer.chunks![index] = undefined as any
        }
      }
    })

    // Filter out undefined chunks to compact the array
    transfer.chunks = transfer.chunks.filter(chunk => chunk !== undefined)
  }

  /**
   * Properly dispose of file reference to prevent memory leaks
   */
  private disposeFileReference(transfer: FileTransfer): void {
    if (!transfer.file) return

    try {
      // If file has stream method, close any associated streams
      if ('stream' in transfer.file && typeof transfer.file.stream === 'function') {
        const stream = transfer.file.stream()
        if (stream && 'cancel' in stream && typeof stream.cancel === 'function') {
          stream.cancel().catch(() => {
            // Ignore cancellation errors
          })
        }
      }

      // Clear the file reference
      delete transfer.file
    } catch (error) {
      // Ignore disposal errors, just clear the reference
      delete transfer.file
    }
  }
  
  /**
   * Clean up resources with comprehensive memory management
   */
  cleanup(): void {
    // MEMORY MANAGEMENT: Properly dispose of all transfer resources
    this.transfers.forEach(transfer => {
      if (transfer.chunks) {
        this.disposeTransferChunks(transfer)
        transfer.chunks = []
      }
      if (transfer.file) {
        this.disposeFileReference(transfer)
        delete transfer.file
      }
    })

    this.transfers.clear()
    this.transferListeners.length = 0

    // Suggest garbage collection if available (for development/testing)
    if (typeof globalThis.gc === 'function') {
      globalThis.gc()
    }

    console.log('FileTransferService cleanup completed')
  }
  
  /**
   * Destroy service and clean up all resources
   */
  destroy(): void {
    this.cleanup()
    super.destroy() // Clean up event listeners
    console.log('FileTransferService destroyed')
  }

  /**
   * Reset service (for testing)
   */
  reset(): void {
    this.cleanup()
    super.destroy() // Clear event listeners
    console.log('FileTransferService reset completed')
  }

  /**
   * UNIVERSAL FIX: Get universal chunk size that works for all device combinations
   * Mobile→Desktop works because mobile uses 8KB chunks that desktop can handle
   * We need to use the same small chunk size for ALL scenarios
   */
  private getOptimalChunkSize(): number {
    // Use 4KB chunks for maximum compatibility across all device combinations
    // This ensures: Desktop→Mobile, Mobile↔Mobile, Desktop↔Desktop all work
    return 4 * 1024 // 4KB universal compatibility
  }

  /**
   * MOBILE FIX: Get maximum concurrent transfers based on device
   */
  private getMaxConcurrentTransfers(): number {
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return isMobile ? 2 : 3 // Fewer concurrent transfers for mobile
  }

  /**
   * MOBILE FIX: Optimize file info for transmission
   */
  private optimizeFileInfo(fileInfo: FileInfo): FileInfo {
    return {
      id: fileInfo.id,
      name: fileInfo.name.length > 100 ? fileInfo.name.substring(0, 97) + '...' : fileInfo.name,
      size: fileInfo.size,
      type: fileInfo.type.length > 50 ? fileInfo.type.substring(0, 50) : fileInfo.type
    }
  }

  /**
   * MOBILE FIX: Validate message size for PeerJS compatibility
   */
  private validateMessageSize(message: any): boolean {
    try {
      const messageSize = JSON.stringify(message).length
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const limit = isMobile ? 8000 : this.MAX_MESSAGE_SIZE // 8KB limit for mobile (4KB chunk + JSON overhead)

      if (messageSize > limit) {
        console.warn(`⚠️ Message size (${messageSize} bytes) exceeds ${isMobile ? 'mobile' : 'desktop'} limit (${limit} bytes)`)
        return false
      }
      return true
    } catch (error) {
      console.error('Error validating message size:', error)
      return false
    }
  }

  /**
   * MOBILE FIX: Validate chunk size for transmission
   */
  private validateChunkSize(chunk: FileChunk): boolean {
    try {
      // Calculate total message size including metadata with truncated IDs
      const metadataSize = JSON.stringify({
        type: chunk.type,
        transferId: chunk.transferId.substring(0, 20), // Truncate to reduce size
        chunkIndex: chunk.chunkIndex,
        totalChunks: chunk.totalChunks,
        timestamp: chunk.timestamp
      }).length

      const totalSize = metadataSize + chunk.data.byteLength
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const limit = isMobile ? 6000 : this.MAX_MESSAGE_SIZE

      if (totalSize > limit) {
        console.warn(`⚠️ Chunk size (${totalSize} bytes) exceeds ${isMobile ? 'mobile' : 'desktop'} limit (${limit} bytes)`)
        return false
      }
      return true
    } catch (error) {
      console.error('Error validating chunk size:', error)
      return false
    }
  }
}